import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Platform, View, ScrollView } from 'react-native';
import { FlashList, FlashListRef } from '@shopify/flash-list';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Pressable } from '@/components/gluestack/pressable';
import { Divider } from '@/components/gluestack/divider';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Tick02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';
import dayjs from '@/i18n/dayjs-config';

export interface TimeValue {
  hours: number;
  minutes: number;
}

export interface IDatePickerSheetProps extends IContextSheetContentProps {
  /** 'date' shows year/month/day pickers, 'time' shows a list of 15-min time slots */
  mode?: 'date' | 'time';
  /** Currently selected date (ISO string or Date) */
  selectedDate?: string | Date | null;
  /** Callback when date is selected */
  onDateChange?: (date: Date) => void;
  /** Maximum selectable date (defaults to 18 years ago for age verification) */
  maxDate?: Date;
  /** Minimum selectable date */
  minDate?: Date;
  /** Title shown in the sheet */
  title?: string;
  /** Currently selected time (for time mode) */
  selectedTime?: TimeValue | null;
  /** Callback when time is selected (for time mode) */
  onTimeChange?: (time: TimeValue) => void;
}

interface PickerOption {
  key: string;
  label: string;
  value: number;
  disabled?: boolean;
}

type PickerMode = 'day' | 'month' | 'year' | 'time';

function formatTimeSlot(hours: number, minutes: number): string {
  const date = new Date(2000, 0, 1, hours, minutes);
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
}

export default function DatePickerSheet({
  onClose,
  payload,
  selectedDate: directSelectedDate,
  onDateChange: directOnDateChange,
  maxDate: directMaxDate,
  minDate: directMinDate,
  mode: directMode,
  selectedTime: directSelectedTime,
  onTimeChange: directOnTimeChange,
}: IDatePickerSheetProps & { payload?: Partial<IDatePickerSheetProps> }) {
  const componentMode = payload?.mode ?? directMode ?? 'date';
  const selectedDateProp = payload?.selectedDate ?? directSelectedDate;
  const onDateChange = payload?.onDateChange ?? directOnDateChange;
  const maxDateProp = payload?.maxDate ?? directMaxDate;
  const minDateProp = payload?.minDate ?? directMinDate;
  const selectedTimeProp = payload?.selectedTime ?? directSelectedTime;
  const onTimeChange = payload?.onTimeChange ?? directOnTimeChange;

  const { t } = useTranslation();

  // Default max date is 18 years ago (for 18+ age requirement)
  const maxDate = useMemo(() => {
    if (maxDateProp) return dayjs(maxDateProp);
    return dayjs().subtract(18, 'year');
  }, [maxDateProp]);

  // Default min date is 100 years ago
  const minDate = useMemo(() => {
    if (minDateProp) return dayjs(minDateProp);
    return dayjs().subtract(100, 'year');
  }, [minDateProp]);

  // Initialize selected date
  const initialDate = useMemo(() => {
    if (selectedDateProp) {
      return dayjs(selectedDateProp);
    }
    // Default to max date (youngest allowed)
    return maxDate;
  }, [selectedDateProp, maxDate]);

  const [currentDate, setCurrentDate] = useState(initialDate);
  const [pickerMode, setPickerMode] = useState<PickerMode>(componentMode === 'time' ? 'time' : 'day');
  const [selectedTimeValue, setSelectedTimeValue] = useState<TimeValue | null>(selectedTimeProp ?? null);

  // Generate time slots (00:00 to 23:45 in 15-min increments)
  const timeSlots = useMemo(() => {
    const result: PickerOption[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const totalMinutes = h * 60 + m;
        result.push({
          key: `time-${totalMinutes}`,
          label: formatTimeSlot(h, m),
          value: totalMinutes,
        });
      }
    }
    return result;
  }, []);

  const selectedTimeIndex = useMemo(() => {
    if (!selectedTimeValue) return -1;
    return timeSlots.findIndex(
      (s) => s.value === selectedTimeValue.hours * 60 + selectedTimeValue.minutes
    );
  }, [selectedTimeValue, timeSlots]);

  const defaultTimeIndex = useMemo(() => {
    return timeSlots.findIndex((s) => s.value === 12 * 60);
  }, [timeSlots]);

  // Generate years list
  const years = useMemo(() => {
    const result: PickerOption[] = [];
    const minYear = minDate.year();
    const maxYear = maxDate.year();
    for (let year = maxYear; year >= minYear; year--) {
      result.push({
        key: `year-${year}`,
        label: year.toString(),
        value: year,
      });
    }
    return result;
  }, [minDate, maxDate]);

  const initialYearIndex = useMemo(() => {
    const idx = years.findIndex((y) => y.value === currentDate.year());
    return Math.max(idx, 0);
  }, [years, currentDate]);

  // Generate months list using dayjs localization
  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => ({
      key: `month-${index}`,
      label: dayjs().month(index).format('MMMM'),
      value: index,
    }));
  }, []);

  // Weekday headers starting from Monday, locale-aware
  const weekdayHeaders = useMemo(() => {
    return [1, 2, 3, 4, 5, 6, 0].map((d) => dayjs().day(d).format('dd'));
  }, []);

  // Number of empty cells before day 1 to align to correct weekday column (Mon-start)
  const firstDayOffset = useMemo(() => {
    const firstDayOfMonth = currentDate.date(1).day(); // 0=Sun, 1=Mon, ..., 6=Sat
    return (firstDayOfMonth + 6) % 7; // Mon=0, Tue=1, ..., Sun=6
  }, [currentDate]);

  // Generate days for current month/year
  const days = useMemo(() => {
    const daysInMonth = currentDate.daysInMonth();
    const result: PickerOption[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = currentDate.date(day);
      const isOutOfRange = date.isAfter(maxDate) || date.isBefore(minDate);
      result.push({
        key: `day-${day}`,
        label: day.toString(),
        value: day,
        disabled: isOutOfRange,
      });
    }
    return result;
  }, [currentDate, maxDate, minDate]);

  const handleYearSelect = useCallback((year: number) => {
    setCurrentDate((prev) => {
      let newDate = prev.year(year);
      // Clamp to valid range
      if (newDate.isAfter(maxDate)) newDate = maxDate;
      if (newDate.isBefore(minDate)) newDate = minDate;
      return newDate;
    });
    setPickerMode('month');
  }, [maxDate, minDate]);

  const handleMonthSelect = useCallback((month: number) => {
    setCurrentDate((prev) => {
      let newDate = prev.month(month);
      // Adjust day if needed
      const daysInMonth = newDate.daysInMonth();
      if (newDate.date() > daysInMonth) {
        newDate = newDate.date(daysInMonth);
      }
      // Clamp to valid range
      if (newDate.isAfter(maxDate)) newDate = maxDate;
      if (newDate.isBefore(minDate)) newDate = minDate;
      return newDate;
    });
    setPickerMode('day');
  }, [maxDate, minDate]);

  const handleDaySelect = useCallback((day: number) => {
    setCurrentDate((prev) => prev.date(day));
  }, []);

  const handleTimeSelect = useCallback((totalMinutes: number) => {
    setSelectedTimeValue({ hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 });
  }, []);

  const handleDone = useCallback(() => {
    if (componentMode === 'time') {
      if (selectedTimeValue) onTimeChange?.(selectedTimeValue);
    } else {
      onDateChange?.(currentDate.toDate());
    }
    onClose?.();
  }, [componentMode, currentDate, selectedTimeValue, onDateChange, onTimeChange, onClose]);

  const renderListItem = useCallback(
    ({ item }: { item: PickerOption }) => {
      let isSelected = false;
      let isDisabled = false;
      let onPress: () => void;

      if (pickerMode === 'year') {
        isSelected = currentDate.year() === item.value;
        onPress = () => handleYearSelect(item.value);
      } else if (pickerMode === 'month') {
        isSelected = currentDate.month() === item.value;
        const monthDate = currentDate.month(item.value);
        isDisabled = monthDate.isAfter(maxDate) || monthDate.endOf('month').isBefore(minDate);
        onPress = () => !isDisabled && handleMonthSelect(item.value);
      } else {
        isSelected = selectedTimeValue
          ? item.value === selectedTimeValue.hours * 60 + selectedTimeValue.minutes
          : false;
        onPress = () => handleTimeSelect(item.value);
      }

      return (
        <Pressable
          onPress={onPress}
          disabled={isDisabled}
          className={`flex-row items-center justify-center px-4 py-3 active:bg-background-50 web:cursor-pointer web:hover:bg-background-50 ${
            isSelected ? 'bg-primary-100' : ''
          } ${isDisabled ? 'opacity-30' : ''}`}>
          <Text size="lg" className={`${isSelected ? 'font-bold text-primary-500' : ''}`} numberOfLines={1}>
            {item.label}
          </Text>
        </Pressable>
      );
    },
    [pickerMode, currentDate, selectedTimeValue, handleYearSelect, handleMonthSelect, handleTimeSelect, maxDate, minDate]
  );

  const renderDayCell = useCallback(
    (item: PickerOption) => {
      const isSelected = !item.disabled && currentDate.date() === item.value;
      const isWeb = Platform.OS === 'web';
      return (
        <Pressable
          key={item.key}
          onPress={() => !item.disabled && handleDaySelect(item.value)}
          disabled={item.disabled}
          className={
            isWeb
              ? `aspect-square items-center justify-center p-2 ${item.disabled ? '' : 'active:bg-background-50 web:cursor-pointer web:hover:bg-background-50'} ${isSelected ? 'bg-primary-100' : ''}`
              : `items-center justify-center p-2 ${item.disabled ? '' : 'active:bg-background-50'} ${isSelected ? 'bg-primary-100' : ''}`
          }
          style={[
            { width: '14.28%' },
            !isWeb && { minHeight: 48 },
          ]}>
          <Text
            size="lg"
            className={`${isSelected ? 'font-bold text-primary-500' : ''} ${item.disabled ? 'text-typography-300' : ''}`}
            numberOfLines={1}
            style={{ textAlign: 'center' }}>
            {item.label}
          </Text>
        </Pressable>
      );
    },
    [currentDate, handleDaySelect]
  );

  const headerLabel = useMemo(() => {
    if (pickerMode === 'time') return t('events.form.selectTime', 'Select Time');
    if (pickerMode === 'year') return t('editProfile.selectYear', 'Select Year');
    if (pickerMode === 'month') return t('editProfile.selectMonth', 'Select Month');
    return currentDate.format('MMMM YYYY');
  }, [pickerMode, currentDate, t]);

  const footerLabel = useMemo(() => {
    if (componentMode === 'time') {
      return selectedTimeValue
        ? formatTimeSlot(selectedTimeValue.hours, selectedTimeValue.minutes)
        : '';
    }
    return currentDate.format('LL');
  }, [componentMode, selectedTimeValue, currentDate]);

  const listRef = useRef<FlashListRef<PickerOption>>(null);

  const handleListLoad = useCallback((scrollIndex: number | undefined) => {
    if (scrollIndex == null || scrollIndex < 0) return;
    setTimeout(() => {
      listRef.current?.scrollToIndex({ index: scrollIndex, animated: false, viewPosition: 0.5 });
    }, 50);
  }, []);

  const isTimeMode = componentMode === 'time';

  return (
    <VStack className="flex-1 gap-2">
      {!isTimeMode && (
        <>
          <HStack className="items-center px-2">
            <View className="flex-1 items-start">
              <Pressable
                onPress={() => setPickerMode('year')}
                className="rounded-lg px-3 py-2 active:bg-background-50 web:cursor-pointer web:hover:bg-background-50">
                <Text size="lg" className="font-semibold text-primary-500">
                  {currentDate.year()}
                </Text>
              </Pressable>
            </View>
            <Pressable
              onPress={() => setPickerMode('month')}
              className="rounded-lg px-3 py-2 active:bg-background-50 web:cursor-pointer web:hover:bg-background-50">
              <Text size="lg" className="font-semibold text-primary-500">
                {currentDate.format('MMMM')}
              </Text>
            </Pressable>
            <View className="flex-1" />
          </HStack>
          <Divider />
        </>
      )}

      {pickerMode !== 'day' && (
        <Text size="sm" className="px-2 text-center text-typography-500">{headerLabel}</Text>
      )}

      <View className="min-h-[250px] flex-1">
        {pickerMode === 'year' && (
          <FlashList<PickerOption>
            ref={listRef}
            data={years}
            renderItem={renderListItem}
            keyExtractor={(item) => item.key}
            onLoad={() => handleListLoad(initialYearIndex)}
            keyboardShouldPersistTaps="handled"
          />
        )}
        {pickerMode === 'month' && (
          <FlashList<PickerOption>
            ref={listRef}
            data={months}
            renderItem={renderListItem}
            keyExtractor={(item) => item.key}
            onLoad={() => handleListLoad(currentDate.month())}
            keyboardShouldPersistTaps="handled"
          />
        )}
        {pickerMode === 'time' && (
          <FlashList<PickerOption>
            ref={listRef}
            data={timeSlots}
            renderItem={renderListItem}
            keyExtractor={(item) => item.key}
            onLoad={() =>
              handleListLoad(selectedTimeIndex >= 0 ? selectedTimeIndex : defaultTimeIndex)
            }
            keyboardShouldPersistTaps="handled"
          />
        )}
        {pickerMode === 'day' && (
          <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ width: '100%' }}>
            <View className="flex-row" style={{ width: '100%' }}>
              {weekdayHeaders.map((label) => (
                <View key={label} className="items-center justify-center py-2" style={{ width: '14.28%' }}>
                  <Text size="xs" className="font-semibold text-typography-400">{label}</Text>
                </View>
              ))}
            </View>
            <View className="flex-row flex-wrap" style={{ width: '100%' }}>
              {Array.from({ length: firstDayOffset }, (_, i) => (
                <View key={`spacer-${i}`} style={{ width: '14.28%' }} />
              ))}
              {days.map(renderDayCell)}
            </View>
          </ScrollView>
        )}
      </View>

      <HStack
        className="items-center justify-between gap-2 border-t border-background-100 px-1 pt-3">
        <Text className="flex-1 text-center text-typography-700">
          {footerLabel}
        </Text>
        <ButtonWrapper icon={Tick02Icon} text={t('common.done', 'Done')} onPress={handleDone} size="sm" />
      </HStack>
    </VStack>
  );
}

DatePickerSheet.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'sm',
  height: 'xl',
  scrollableContent: true,
} satisfies ISheetConfig;
